<template>
  <div>
    <h3 class="mb-3 input-text-size">Export Mortgage Data</h3>
    <div class="pb-2">
      <v-container class="py-0 px-0">
        <v-row dense>
          <v-col cols="12" md="6">
            <v-select
              v-model="selectedFileDefinition"
              :items="fileDefinitions"
              item-text="Name"
              item-value="_id"
              label="File Definition"
              outlined
              dense
              :loading="loadingDefinitions"
              :disabled="loadingDefinitions"
            />
          </v-col>
          <v-col cols="12" md="6">
            <v-select
              v-model="selectedTaxYear"
              :items="availableTaxYears"
              label="Tax Year"
              outlined
              dense
            />
          </v-col>
        </v-row>
        <v-row dense>
          <v-col cols="12">
            <v-checkbox
              v-model="includePriorYearUnpaid"
              label="Include Prior Year Unpaid"
              dense
              class="mt-0"
            />
          </v-col>
        </v-row>
        <v-row dense>
          <v-col cols="12">
            <v-alert
              v-if="errorMessage"
              type="error"
              dense
              dismissible
              @input="errorMessage = null"
            >
              {{ errorMessage }}
            </v-alert>
            <v-alert
              v-if="successMessage"
              type="success"
              dense
              dismissible
              @input="successMessage = null"
            >
              {{ successMessage }}
            </v-alert>
          </v-col>
        </v-row>
      </v-container>
    </div>
    <div class="mt-3">
      <v-btn
        color="primary"
        small
        rounded
        class="input-text-size"
        :disabled="!selectedFileDefinition"
        @click="downloadSpec"
      >
        Download Spec
      </v-btn>
      <v-btn
        color="primary"
        small
        rounded
        class="input-text-size ml-2"
        :disabled="!canQueueExport"
        :loading="queueing"
        @click="queueExport"
      >
        Queue Export
      </v-btn>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { clients } from '../../../util/clients';

export default {
  name: 'ExportForm',
  data() {
    return {
      selectedFileDefinition: null,
      selectedTaxYear: null,
      includePriorYearUnpaid: true,
      fileDefinitions: [],
      loadingDefinitions: false,
      queueing: false,
      errorMessage: null,
      successMessage: null,
      baseCrudKey: 'fileDefinitionsBaseCrudKey',
    };
  },
  computed: {
    ...mapGetters('base/crud', ['items']),
    availableTaxYears() {
      const currentYear = new Date().getFullYear();
      const years = [];
      for (let i = currentYear; i >= currentYear - 10; i -= 1) {
        years.push(i);
      }
      return years;
    },
    canQueueExport() {
      return this.selectedFileDefinition && this.selectedTaxYear && !this.queueing;
    },
    selectedDefinition() {
      return this.fileDefinitions.find((def) => def._id === this.selectedFileDefinition);
    },
  },
  async mounted() {
    await this.loadFileDefinitions();
    this.selectedTaxYear = new Date().getFullYear();
  },
  methods: {
    ...mapActions('config', {
      loadFileDefinitionsAction: 'loadFileDefinitions',
    }),
    ...mapActions('fileQueue', [
      'upsertFileQueue',
    ]),
    async loadFileDefinitions() {
      try {
        this.loadingDefinitions = true;
        await this.loadFileDefinitionsAction({
          criteria: {
            Program: 'MTGEXPORT',
          },
          fields: {
            _id: 1,
            Name: 1,
            Program: 1,
            FileType: 1,
            ColumnDefinition: {
              ColumnNumber: 1,
              Name: 1,
              MapToTable: 1,
              MapToColumn: 1,
              StartPosition: 1,
              Length: 1,
              FieldType: 1,
            },
          },
          baseCrudKey: this.baseCrudKey,
        });
        // Get from store after it's loaded
        this.fileDefinitions = this.items(this.baseCrudKey) || [];
      } catch (error) {
        console.error('Error loading file definitions:', error);
        this.errorMessage = 'Failed to load file definitions';
      } finally {
        this.loadingDefinitions = false;
      }
    },
    async downloadSpec() {
      if (!this.selectedDefinition) {
        return;
      }

      try {
        const definition = this.selectedDefinition;
        const filename = `${definition.Name.replace(/[^a-z0-9]/gi, '_')}_spec.txt`;

        // Use axios with blob response type (same pattern as FileDefinitionSetting)
        const { backendRest } = clients.direct;
        const response = await backendRest.get(
          `/fileDefinition/download/${definition._id}`,
          {
            responseType: 'blob',
          },
        );

        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error('Error downloading spec:', error);
        this.errorMessage = 'Failed to download specification';
      }
    },
    async queueExport() {
      try {
        this.queueing = true;
        this.errorMessage = null;
        this.successMessage = null;

        const definition = this.selectedDefinition;
        if (!definition) {
          this.errorMessage = 'Please select a file definition';
          return;
        }

        const timestamp = new Date();
        const dateStr = timestamp.toISOString().slice(0, 10).replace(/-/g, '');
        const timeStr = timestamp.toTimeString().slice(0, 8).replace(/:/g, '');
        const fileName = `${definition.Name}_${dateStr}_${timeStr}.${definition.FileType === 'Fixed Width' ? 'txt' : 'csv'}`;

        const queueItem = {
          timestamp,
          FileName: fileName,
          Program: 'MTGEXPORT',
          FileDefinitionId: this.selectedFileDefinition,
          FileDefinitionName: definition.Name,
          Status: 'Pending',
          Records: 0,
          TotalRecords: 0,
          Errors: 0,
          Parameters: {
            taxYear: this.selectedTaxYear,
            includePriorYearUnpaid: this.includePriorYearUnpaid,
          },
        };

        await this.upsertFileQueue({
          item: queueItem,
          fields: {
            _id: 1,
          },
        });

        this.successMessage = 'Export queued successfully';
        this.$emit('export-queued');
      } catch (error) {
        console.error('Error queueing export:', error);
        this.errorMessage = 'Failed to queue export';
      } finally {
        this.queueing = false;
      }
    },
  },
};
</script>

<style scoped>
</style>
